/**
 * What the assistant panel deals in.
 *
 * <p>Three shapes, and the distinction between them is the whole governance story: a
 * {@link AssistantMessage} is what was said, a tool call is something the assistant *read* on
 * the signed-in user's behalf, and an {@link AssistantProposal} is a write it wants to make
 * and cannot until a person presses Confirm.
 */

export type AssistantRole = 'SYSTEM' | 'USER' | 'ASSISTANT' | 'TOOL'

export type AssistantFeedback = 'UP' | 'DOWN'

export type ProposalStatus = 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'EXPIRED' | 'FAILED'

/** Which family a tool belongs to, as the settings screen groups them. */
export type ToolGroup = 'SELF' | 'LOOKUP' | 'PROPOSAL'

export interface AssistantConversationView {
  id: string
  title: string | null
  locale: string | null
  provider: string | null
  model: string | null
  messageCount: number
  toolCallCount: number
  inputTokens: number
  outputTokens: number
  feedback: AssistantFeedback | null
  lastMessageAt: string | null
}

export interface AssistantMessageView {
  id: string
  seq: number
  role: AssistantRole
  content: string | null
  toolCalls: Record<string, unknown>[] | null
  /**
   * Hashes and row counts, never the rows.
   *
   * The server deliberately does not store what a tool returned, so a reopened conversation
   * can say "looked up your leave balance" but cannot replay the balance. The panel says so
   * rather than rendering an empty space that looks like a bug.
   */
  toolResults: Record<string, unknown>[] | null
  redacted: boolean
  model: string | null
  latencyMs: number | null
  error: string | null
  createdAt: string
}

export interface AssistantConversationDetail {
  conversation: AssistantConversationView
  messages: AssistantMessageView[]
}

export interface AssistantProposalView {
  id: string
  toolName: string
  arguments: Record<string, unknown>
  summary: string | null
  status: ProposalStatus
  expiresAt: string | null
  resultEntityType: string | null
  resultEntityId: string | null
  refusal: string | null
}

/**
 * One frame of the stream.
 *
 * <p>Named after the server's sealed `Event` subtypes, lowercased, because that is what the
 * SSE `event:` line carries.
 */
export type AssistantStreamEvent =
  | { type: 'text'; delta: string }
  | { type: 'toolstarted'; tool: string }
  | { type: 'toolfinished'; tool: string; outcome: string }
  | { type: 'proposal'; proposalId: string; tool: string; summary: string }
  | { type: 'done'; conversationId: string; messageId: string }
  | { type: 'failed'; reason: string }

/**
 * Where each proposal's result lives, so a confirmed one can offer "see it".
 *
 * <p>These are read out of `src/app/router.tsx`, not composed from the tool names. The two
 * do not match — the screen is `/attendance/regularisation`, singular and spelt the British
 * way, and helpdesk has no `/tickets` segment at all — so a plausible-looking path would have
 * shipped as a link to a blank page on the one screen a person reaches after confirming
 * something.
 */
export const PROPOSAL_DESTINATION: Record<string, { label: string; path: string }> = {
  propose_leave: { label: 'Open leave', path: '/leave' },
  propose_regularisation: { label: 'Open regularisation', path: '/attendance/regularisation' },
  propose_expense: { label: 'Open expense claims', path: '/expenses/claims' },
  propose_ticket: { label: 'Open helpdesk', path: '/helpdesk' },
  propose_approval_decision: { label: 'Open approvals', path: '/approvals' },
}

/**
 * How a tool call reads to a person.
 *
 * <p>The panel shows what the assistant looked at, in the user's words, because "it read
 * something" is not a meaningful disclosure and the tool name is not English.
 */
export const TOOL_LABELS: Record<string, string> = {
  my_profile: 'your profile',
  my_leave_balance: 'your leave balance',
  my_leave_requests: 'your leave requests',
  my_payslip: 'your payslip',
  my_attendance: 'your attendance',
  my_documents: 'your documents',
  my_requests: 'your requests',
  my_pending_approvals: 'approvals waiting for you',
  my_team: 'your team',
  find_employee: 'the employee directory',
  employee_summary: 'a colleague’s profile',
  document_expiries: 'documents expiring soon',
  headcount: 'headcount',
  open_positions: 'open positions',
  upcoming_holidays: 'the holiday calendar',
  policy_search: 'published policies',
}

export function toolLabel(name: string): string {
  return TOOL_LABELS[name] ?? name.replace(/_/g, ' ')
}
